import { useState, useRef, useEffect } from 'react';
import Spinner from '../common/Spinner';
import Badge from '../common/Badge';
import { api } from '../../services/apiClient';

export default function DriveScanModal({ type, onDone, onClose, toast }) {
  const [folderId, setFolderId] = useState('');
  const [job, setJob] = useState(null);
  const [scanning, setScanning] = useState(false);
  const pollRef = useRef();

  async function startScan() {
    if (!folderId.trim()) return;
    setScanning(true);
    try {
      const res = await api.driveScan({ folderId: folderId.trim(), type });
      pollJob(res.jobId);
    } catch (e) {
      toast('Scan failed: ' + e.message, 'error');
      setScanning(false);
    }
  }

  function pollJob(id) {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const j = await api.driveJob(id);
        setJob(j);
        if (j.status === 'done' || j.status === 'error') {
          clearInterval(pollRef.current);
          setScanning(false);
          if (j.status === 'done') {
            toast(`Drive scan complete — ${j.invoiceIds?.length || 0} invoice(s) saved`, 'success');
            onDone();
          }
        }
      } catch (e) {}
    }, 2000);
  }

  useEffect(() => () => clearInterval(pollRef.current), []);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <span className="modal-title">📁 Scan Google Drive (Recursive)</span>
          <button className="btn btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 12 }}>
            Point this at a top-level shared folder (e.g. "FIS suppliers sale record"). The system walks every subfolder automatically, finds every folder that directly contains receipt images, resolves a date for each folder (date-named subfolder first, else the receipt's own date), and saves <code>invoice-DD-MM-YYYY.xlsx</code> back into each source folder. Forgotten/backdated receipts are detected and recalculated automatically — no date needs to be entered here.
          </div>

          <div className="form-group">
            <label className="form-label">Root Drive Folder ID</label>
            <input
              className="form-input"
              placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs"
              value={folderId}
              onChange={e => setFolderId(e.target.value)}
              disabled={scanning}
            />
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 5 }}>
              Get from the Drive folder URL: drive.google.com/drive/folders/<strong>THIS_PART</strong>
            </div>
          </div>

          {job && (
            <div className="job-card" style={{ marginTop: 4 }}>
              <div className="job-header">
                <span className="job-title">Scan Progress</span>
                <Badge variant={job.status === 'done' ? 'success' : job.status === 'error' ? 'danger' : 'warning'}>
                  {job.status === 'processing' && <Spinner />} {job.status}
                </Badge>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 6 }}>
                Folders: {job.foldersProcessed || 0}/{job.foldersFound || 0} · Files: {job.filesProcessed || 0}/{job.filesFound || 0}
                {job.invoiceIds?.length > 0 && <> · Invoices saved: {job.invoiceIds.length}</>}
              </div>
              <div className="job-log">
                {job.log?.map((l, i) => (
                  <div key={i} className={`job-log-entry ${l.msg?.includes('✓') || l.msg?.includes('✅') ? 'ok' : l.msg?.includes('✗') || l.msg?.startsWith('ERROR') ? 'err' : ''}`}>
                    {new Date(l.ts).toLocaleTimeString()} — {l.msg}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose} disabled={scanning}>Close</button>
          <button className="btn btn-primary" onClick={startScan} disabled={!folderId || scanning}>
            {scanning ? <><Spinner /> Scanning...</> : '🔍 Start Recursive Scan'}
          </button>
        </div>
      </div>
    </div>
  );
}
